import { SubscriptionOptions } from '@reduxjs/toolkit/dist/query/core/apiState';
import ServerPagination from 'Components/Pagination/ServerPagination';
import { createContext, ReactNode, useEffect, useMemo } from 'react';
import {
  PaginationHandles,
  PaginationQueryParams,
  UsePaginationLazyQuery,
  UsePaginationReturn,
  UsePaginationReturn1,
} from './types';
import useCachePagination from './useCachePagination';

export const PaginationContext = createContext<
  Omit<UsePaginationReturn<any>, 'PaginationContextProvider'>
>({} as any);

export default function usePagination<Q extends UsePaginationLazyQuery>(
  lazyQuery: Q,
  params: PaginationQueryParams<Q>,
  options: { skip?: boolean; skipQueryParamsCache?: boolean } = {
    skip: false,
    skipQueryParamsCache: false,
  },
  subscriptionOptions?: SubscriptionOptions,
): UsePaginationReturn1<Q> {
  const {
    GetData,
    allPages,
    current,
    pagination,
    isFetching,
    isLoading,
    queryParams,
    setFilters,
    data,
    ...HistoryActions
  } = useCachePagination(
    lazyQuery,
    {
      allPages: [],
      pagination: {
        count: 0,
        pages: 0,
      },
      queryParams: {
        per_page: params.per_page ? params.per_page : 1,
        page: 1,
        filters: params.filters,
      },
    },
    subscriptionOptions,
    options.skipQueryParamsCache,
  );

  const currentPageData = current?.data;

  const GetPage = (
    page: number,
    cache = allPages,
    per_page = queryParams.per_page,
  ) => {
    !options.skip && GetData({ ...queryParams, page, per_page }, cache);
  };

  const setper_page = (per_page: number) => {
    GetData(
      {
        ...queryParams,
        page: 1,
        per_page,
      },
      [],
    );
  };

  useEffect(() => GetPage(queryParams.page), [options.skip]);

  const refetchSamePage = (page?: number) => {
    let p = !!page ? page : queryParams.page;
    let cache = allPages.filter((cp) => cp.page !== p);
    GetPage(p, cache);
  };

  const refetchAfterDelete = () => {
    if (!currentPageData) return;
    let currentPage = current.page;
    let newcache = allPages.filter((p) => current.page < currentPage);
    const isOnLastPage = pagination.pages === currentPage;
    const hasOneItem = currentPageData.length === 1;

    let goBack = isOnLastPage && hasOneItem && currentPage > 1;

    GetPage(goBack ? currentPage - 1 : currentPage, newcache);
  };

  const refetchAfterAdd = (
    total = pagination.count,
    per_page = queryParams.per_page ? queryParams.per_page : 1,
  ) => {
    if (!currentPageData) return;

    const newItemPage = Math.ceil((total + 1) / per_page);

    let newCache = allPages.filter((p) => p.page != newItemPage);

    GetPage(newItemPage, newCache);
  };

  const goToFirstPage = () => GetPage(1, []);

  const Pagination = useMemo(() => {
    return () => (
      <ServerPagination
        onper_pageChange={setper_page}
        onPageChange={GetPage}
        pagination={{
          count: pagination.count,
          pages: pagination.pages,
          page: queryParams.page,
          per_page: queryParams.per_page,
        }}
      />
    );
  }, [current, queryParams.page]);
  const updateQueryParamValue = <
    Key extends keyof PaginationQueryParams<Q>['filters'],
  >(
    key: Key,
    value: PaginationQueryParams<Q>['filters'][Key],
    checkPoint = false,
  ) => {
    setFilters({ ...queryParams.filters, [key]: value }, checkPoint);
  };
  const API = {
    Pagination,
    filters: queryParams.filters,
    setFilters,
    updateQueryParamValue,
    page: queryParams.page,
    per_page: queryParams.per_page,
    totalCount: pagination.count,
    data: currentPageData,
    allPages,
    GetPage,
    pagination,
    refetchAfterAdd,
    refetchAfterDelete,
    refetchSamePage,
    goToFirstPage,
    isLoading,
    isFetching,
    response: data,
    ...HistoryActions,
  };
  const PaginationContextProvider = useMemo(() => {
    return ({ children }: { children: ReactNode }) => (
      <PaginationContext.Provider
        // @ts-ignore
        value={API}
      >
        {children}
      </PaginationContext.Provider>
    );
  }, [refetchAfterAdd, refetchAfterDelete, refetchSamePage]);

  // @ts-ignore
  return { PaginationContextProvider, ...API };
}
